import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {onOpenCropModal, onOpenMaskingModal, onOpenReplaceMedia} from '@Components/poster-editor/library/poster-editor-open-modals';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {ContextualMenuReplaceMediaControls} from '@Components/contextual-menu/components/contextual-menu-replace-media-controls';
import {removeImageBackgroundFromId, removeImageSlideBackgroundFromId} from '@Components/poster-editor/library/poster-editor-library';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {RemoveImageBackgroundNotificationTag} from '@Components/poster-editor/components/remove-image-background-notification-tag';
import {doesSupportRemoveBackground} from '@Libraries/image-item.library';
import styles from './base-image-media-controls.module.scss';
import {
    getCreditsForImageBGR,
    isImageBGRAllowedForPAYGUser
} from '@Libraries/bg-removals-library';
import {FreeBgrPopup} from '@Components/free-bgr-popup';
import {isUserPremium} from '@Libraries/user.library';

interface BaseImageMediaControlsProps {
  activeItem: ImageItem | ImageSlideItem;

  onReplaceImage(itemData: ItemData): void;
}

enum ImageMediaControlsOptions {
  CROP = 'crop',
  REPLACE = 'replace',
  REMOVE_BG = 'remove-bg',
  MASK = 'mask',
}

export function BaseImageMediaControls({...props}: BaseImageMediaControlsProps): ReactElement | null {
  const [isReplaceOptionEnabled, setIsReplaceOptionEnabled] = useState(false);
  const backgroundRemovalCredits = getCreditsForImageBGR();
    const isBGRAllowedForPAYG = isImageBGRAllowedForPAYGUser();

  if (props.activeItem.isDrawing()) {
    return null;
  }

  String(backgroundRemovalCredits);

  if (!props.activeItem || (props.activeItem && !('cropData' in props.activeItem))) {
    return null;
  }

  const onClickCrop = (): void => {
    onOpenCropModal(props.activeItem);
  };

  const onClickRemoveBG = async (): Promise<void> => {
    if (props.activeItem.removeBackground.isBackgroundRemoved && !props.activeItem.removeBackground.isBackgroundBeingRemoved) {
      await props.activeItem.disableRemoveBackgroundFlag();
      return;
    }

    if (props.activeItem.isImageSlide()) {
      removeImageSlideBackgroundFromId(props.activeItem.slideshow.uid, props.activeItem.uid);
    } else {
      removeImageBackgroundFromId(props.activeItem.uid);
    }
  };

  const onClickMask = async (): Promise<void> => {
    return onOpenMaskingModal(props.activeItem);
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceMedia(id, props.activeItem, (itemData: ItemData): void => {
      props.onReplaceImage(itemData);
    });
  };

  const getCustomPopupForBGR = (): ReactElement | undefined => {
    if (!isUserPremium() && isBGRAllowedForPAYG && backgroundRemovalCredits > 0) {
      if (props.activeItem.removeBackground.isBackgroundRemoved || props.activeItem.removeBackground.isBackgroundBeingRemoved) {
        return undefined;
      }

      return <FreeBgrPopup />;
    }

    return undefined;
  };

  const getItems = (): Array<MediaControlsItemProps> => {
    const items: Array<MediaControlsItemProps> = [
      {
        id: `${ImageMediaControlsOptions.CROP}-media-control`,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onClickCrop,
      },
      {
        id: `${ImageMediaControlsOptions.REPLACE}-media-control`,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        onClick: (): void => {
          setIsReplaceOptionEnabled(true);
        },
      },
    ];

    if (doesSupportRemoveBackground(props.activeItem.imageSource)) {
      items.push({
        id: `${ImageMediaControlsOptions.REMOVE_BG}-media-control`,
        icon: 'icon-remove-bg',
        text: window.i18next.t('pmwjs_ai_background_remover'),
        isLoading: props.activeItem.removeBackground.isBackgroundBeingRemoved,
        loadingText: window.i18next.t('pmwjs_removing_background'),
        tag: <RemoveImageBackgroundNotificationTag hashedFilename={props.activeItem.hashedFilename} imageSource={props.activeItem.imageSource} />,
        isSelected: props.activeItem.removeBackground.isBackgroundRemoved && !props.activeItem.removeBackground.isBackgroundBeingRemoved,
        selectedClasses: styles.aiBGLight,
        onClick: (): void => {
          void onClickRemoveBG();
        },
        textColorClasses: styles.aiText,
        textPaddingClasses: 'spacing-p-r-4 spacing-p-l-4',
        isTextBold: true,
        iconClasses: styles.aiText,
        tagContainerClassName: '_full-width',
        isDisabled: props.activeItem.isImageUploading(),
        customPopup: getCustomPopupForBGR(),
      });
    }

    items.push({
      id: `${ImageMediaControlsOptions.MASK}-media-control`,
      icon: 'icon-mask',
      text: window.i18next.t('pmwjs_mask'),
      isSelected: props.activeItem.isMasked(),
      onClick: (): void => {
        void onClickMask();
      },
    });

    return items;
  };

  return isReplaceOptionEnabled ? (
    <ContextualMenuReplaceMediaControls
      id="background-image-replace-media-controls"
      controlsClassName={styles.replaceOption}
      onClick={onReplaceCallback}
      onCancel={(): void => {
        setIsReplaceOptionEnabled(false);
      }}
    />
  ) : (
    <ContextualMenuMediaControlOptions id="image-media-controls" items={getItems()} />
  );
}
